import * as React from "react"
import { useParams } from "react-router"

import { Alert, Skeleton } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { useToast } from "@/components/ui/toast"
import { bannerFor } from "@/lib/api-error-message"

import { AddFindingDialog, type AddFindingInput, type DrawContext } from "./findings/add-finding-dialog"
import {
  useAddFindingMutation,
  useAdvanceStageMutation,
  useArchiveReviewMutation,
  useAssignReviewMutation,
  useEditNarrativeMutation,
  useLiveInvalidation,
  useMarkCorrectedMutation,
  useMarkMessagesReadMutation,
  usePostMessageMutation,
  useReturnToPoolMutation,
  useReviewLive,
  useReviewQuery,
  useSetVerdictMutation,
  useSkipSecondPassMutation,
} from "./api"
import { useReviewCommentsQuery } from "./api/use-review-comments"
import { useReviewHistoryQuery } from "./api/use-review-history"
import { useReviewPeopleQuery } from "./api/use-review-people"
import { FindingsRail } from "./findings/findings-rail"
import { MessagesSheet } from "./messages/messages-sheet"
import { PdfControls } from "./pdf/pdf-controls"
import { PdfSurface } from "./pdf/pdf-surface"
import { PeopleRoutingSheet } from "./people/people-routing-sheet"
import { ReviewBlockerStrip } from "./review-blocker-strip"
import { ReviewHeader, type ReviewViewMode } from "./review-header"
import { ReviewShortcuts } from "./review-shortcuts"
import {
  hasSpecialistBlocker,
  type AssignmentEvent,
  type FindingId,
  type Message,
  type Review,
  type ReviewId,
  type Role,
  type UserId,
  VERDICT_LABEL,
} from "./data/review-data"
import { PAGE_SECTIONS, RULE_CATALOG } from "./data/rule-catalog"
import { SideBySide } from "./pdf/side-by-side"
import { SplitPane } from "./pdf/split-pane"
import { useReviewHotkeys } from "./use-review-hotkeys"

/**
 * Review workspace route component.
 *
 * This file is composition, not logic. It owns the local UI state that
 * spans the rail and the document surface (active finding, expanded and
 * editing pointers, view mode, page and zoom) and dispatches user
 * intents to the API layer. Every feature-level concern — data
 * fetching, mutations, realtime, keyboard, rendering — lives in a
 * sibling module.
 *
 * Data flow: `useReviewQuery` yields the review; mutation hooks apply
 * optimistic updates via the shared cache and roll back on failure.
 * Local UI state is intentionally not persisted — a fresh mount is
 * always a fresh session.
 */
export function ReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const reviewId = (id ?? null) as ReviewId | null
  const query = useReviewQuery(reviewId)

  useLiveInvalidation(reviewId, query.refetch)

  if (query.isSuccess && query.data === null) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <EmptyState
          reason="empty"
          title="Review not found"
          body="The link you followed points at a review that has been archived, or never existed. Head back to the queue to pick another."
        />
      </div>
    )
  }

  if (query.isError) {
    return <QueryErrorState error={query.error} onRetry={query.refetch} />
  }

  if (query.isPending || !query.data) return <WorkspaceSkeleton />

  return <Workspace key={query.data.id} review={query.data} />
}

function QueryErrorState({
  error,
  onRetry,
}: {
  error: unknown
  onRetry: () => void
}): JSX.Element {
  const banner = bannerFor(error, "this review")
  return (
    <div className="flex h-full items-center justify-center p-8">
      <EmptyState
        reason="error"
        title={banner.title || "Couldn't load this review"}
        body={
          banner.body ||
          "We reached the server but it did not send the review back. Try again in a moment."
        }
        action={
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    </div>
  )
}

const EMPTY_COLLEAGUES: ReadonlyArray<{ userId: UserId; name: string; role: Role }> = []
const EMPTY_MESSAGES: readonly Message[] = []
const EMPTY_HISTORY: readonly AssignmentEvent[] = []

function Workspace({ review }: { review: Review }): JSX.Element {
  const reviewId = review.id
  const { toast } = useToast()

  const live = useReviewLive(reviewId)

  const { data: people } = useReviewPeopleQuery(reviewId)
  const colleagues = people?.assignable ?? EMPTY_COLLEAGUES

  const { data: conversation } = useReviewCommentsQuery(reviewId)
  const messages = conversation?.messages ?? EMPTY_MESSAGES
  const totalUnread = conversation?.totalUnread ?? 0

  const resolveName = React.useCallback(
    (userId: number | null): string | null => {
      if (userId == null) return null
      const key = String(userId)
      const pool = [
        people?.assignee,
        people?.owner,
        ...(people?.clientContacts ?? []),
        ...(people?.assignable ?? []),
      ]
      for (const c of pool) {
        if (c && c.userId === key) return c.name
      }
      return null
    },
    [people],
  )
  const { data: history } = useReviewHistoryQuery(reviewId, resolveName)
  const events = history ?? EMPTY_HISTORY

  const setVerdict = useSetVerdictMutation()
  const markCorrected = useMarkCorrectedMutation()
  const editNarrative = useEditNarrativeMutation()
  const addFinding = useAddFindingMutation()
  const assignReview = useAssignReviewMutation()
  const returnToPool = useReturnToPoolMutation()
  const advanceStage = useAdvanceStageMutation()
  const skipSecondPass = useSkipSecondPassMutation()
  const archiveReview = useArchiveReviewMutation()
  const postMessage = usePostMessageMutation()
  const markMessagesRead = useMarkMessagesReadMutation()

  /* Any mutation error is surfaced as a single top-of-page banner so
     the user does not need to hunt for which action failed. */
  const globalError =
    setVerdict.error ??
    markCorrected.error ??
    editNarrative.error ??
    addFinding.error ??
    assignReview.error ??
    returnToPool.error ??
    advanceStage.error ??
    skipSecondPass.error ??
    archiveReview.error ??
    postMessage.error ??
    null

  const dismissGlobalError = React.useCallback(() => {
    setVerdict.reset()
    markCorrected.reset()
    editNarrative.reset()
    addFinding.reset()
    assignReview.reset()
    returnToPool.reset()
    advanceStage.reset()
    skipSecondPass.reset()
    archiveReview.reset()
    postMessage.reset()
  }, [
    setVerdict,
    markCorrected,
    editNarrative,
    addFinding,
    assignReview,
    returnToPool,
    advanceStage,
    skipSecondPass,
    archiveReview,
    postMessage,
  ])

  const [view, setView] = React.useState<ReviewViewMode>("findings")
  const [activeId, setActiveId] = React.useState<FindingId | null>(null)
  const [expandedId, setExpandedId] = React.useState<FindingId | null>(null)
  const [editingId, setEditingId] = React.useState<FindingId | null>(null)
  const [peopleOpen, setPeopleOpen] = React.useState(false)
  const [messagesOpen, setMessagesOpen] = React.useState(false)
  const [addFindingOpen, setAddFindingOpen] = React.useState(false)
  const [drawMode, setDrawMode] = React.useState(false)
  const [drawContext, setDrawContext] = React.useState<DrawContext | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [zoom, setZoom] = React.useState(1)
  // Focus request: nonce-bumped so re-focusing the same finding still fires.
  // PdfSurface owns the two-phase (setZoom → wait for render → scroll) so the
  // scroll never runs against a layout that hasn't landed yet.
  const [focusReq, setFocusReq] = React.useState<{ findingId: FindingId; nonce: number } | null>(
    null,
  )
  // The zoom we were at before the last focus, so the pill can revert to it.
  const [preFocusZoom, setPreFocusZoom] = React.useState<number | null>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const clientReportBlocked = hasSpecialistBlocker(review.findings)
  const specialistCount = React.useMemo(
    () => review.findings.filter((f) => f.axes.verdict === "needs_sme").length,
    [review.findings],
  )

  const orderedFindings = React.useMemo(
    () =>
      [...review.findings].sort((a, b) => {
        if (a.group.ordinal !== b.group.ordinal)
          return a.group.ordinal - b.group.ordinal
        return a.number - b.number
      }),
    [review.findings],
  )

  const focusFinding = React.useCallback(
    (id: FindingId) => {
      setActiveId(id)
      setExpandedId(id)
      setEditingId(null)
      const f = orderedFindings.find((x) => x.id === id)
      if (f?.bbox) setPage(f.bbox.page)
      // Selection alone doesn't zoom — the reviewer opts in via the pill.
      // Changing selection while framed unframes: the pill's revert-zoom
      // would otherwise send them to a zoom they no longer have in mind.
      setPreFocusZoom(null)
      // Scroll the rail row into view. The PDF box is scrolled into view by
      // PdfSurface itself, which knows when its own layout has settled.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-finding-id="${CSS.escape(id)}"]`)
        el?.scrollIntoView({ behavior: "smooth", block: "center" })
      })
    },
    [orderedFindings],
  )

  const zoomToActive = React.useCallback(() => {
    const active = orderedFindings.find((f) => f.id === activeId)
    if (!active?.bbox) return
    if (preFocusZoom != null) {
      // Revert: PdfSurface has served the last focus, we're framed. Restore
      // the zoom the reviewer had before that focus.
      setZoom(preFocusZoom)
      setPreFocusZoom(null)
      return
    }
    // First click: remember where we were, then hand a focus request to
    // PdfSurface. It computes a per-finding zoom that fits the bbox to the
    // viewport (a tiny target zooms more than a large one), then scrolls it
    // to the middle once the render has actually landed at that zoom.
    setPreFocusZoom(zoom)
    setFocusReq((prev) => ({ findingId: active.id, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [orderedFindings, activeId, zoom, preFocusZoom])

  const setZoomManual = React.useCallback((z: number) => {
    setZoom(z)
    setPreFocusZoom(null)
  }, [])

  const step = React.useCallback(
    (delta: -1 | 1) => {
      if (orderedFindings.length === 0) return
      const idx = orderedFindings.findIndex((f) => f.id === activeId)
      const nextIdx =
        idx < 0
          ? delta === 1
            ? 0
            : orderedFindings.length - 1
          : (idx + delta + orderedFindings.length) % orderedFindings.length
      const target = orderedFindings[nextIdx]
      if (target) focusFinding(target.id)
    },
    [orderedFindings, activeId, focusFinding],
  )

  const doVerdict = React.useCallback(
    (findingId: FindingId, verdict: "confirmed" | "rejected" | "needs_sme") => {
      setVerdict.mutate({ reviewId, findingId, verdict })
      const label =
        verdict === "confirmed"
          ? "Confirmed"
          : verdict === "rejected"
            ? "Dismissed"
            : "Sent to a specialist"
      const tone: "success" | "neutral" | "info" =
        verdict === "confirmed" ? "success" : verdict === "rejected" ? "neutral" : "info"
      toast({
        tone,
        title: label,
        action: {
          label: "Undo",
          onAction: () => setVerdict.undo({ reviewId, findingId }),
        },
      })
    },
    [reviewId, setVerdict, toast],
  )

  const withActive = React.useCallback(
    (fn: (id: FindingId) => void) => {
      if (activeId) fn(activeId)
    },
    [activeId],
  )

  useReviewHotkeys(
    {
      onNext: () => step(1),
      onPrev: () => step(-1),
      onConfirm: () => withActive((id) => doVerdict(id, "confirmed")),
      onDismiss: () => withActive((id) => doVerdict(id, "rejected")),
      onSendToSme: () => withActive((id) => doVerdict(id, "needs_sme")),
      onMarkCorrected: () =>
        withActive((id) => markCorrected.mutate({ reviewId, findingId: id })),
      onEnterEdit: () =>
        withActive((id) => {
          const f = orderedFindings.find((x) => x.id === id)
          if (f?.authoredBy) setEditingId(id)
        }),
      onCollapse: () => {
        setExpandedId(null)
        setEditingId(null)
      },
      onFocusSearch: () => searchRef.current?.focus(),
      onOpenShortcuts: () => setShortcutsOpen(true),
    },
    { paused: peopleOpen || messagesOpen || addFindingOpen || shortcutsOpen },
  )

  const openComments = React.useCallback((id: FindingId) => {
    setActiveId(id)
    setMessagesOpen(true)
  }, [])

  const submitAddFinding = React.useCallback(
    (input: AddFindingInput) => {
      addFinding.mutate({ reviewId, input })
      toast({ tone: "success", title: `Finding ${input.ruleId} added` })
    },
    [addFinding, reviewId, toast],
  )

  // Autoscroll + expand a newly-created finding so the user never hunts
  // for the record they just added.
  const previousCreated = React.useRef<FindingId | null>(null)
  React.useEffect(() => {
    const created = addFinding.lastCreatedId
    if (created && created !== previousCreated.current) {
      previousCreated.current = created
      focusFinding(created)
    }
  }, [addFinding.lastCreatedId, focusFinding])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ReviewHeader
        productName={review.productName}
        epaRegNo={review.epaRegNo}
        version={review.version}
        liveState={live.status}
        presence={live.presence}
        view={view}
        onViewChange={setView}
        unreadMessageCount={totalUnread}
        messages={messages}
        findings={review.findings}
        clientReportBlocked={clientReportBlocked}
        onOpenPeople={() => setPeopleOpen(true)}
        onOpenMessages={() => setMessagesOpen(true)}
        onOpenClientReport={() =>
          toast({
            tone: "info",
            title: "Client report",
            description: "Preview + send lands with the report screen.",
          })
        }
        backHref="/reviews"
      />

      {clientReportBlocked && (
        <ReviewBlockerStrip
          message={
            specialistCount === 1
              ? `One finding is ${VERDICT_LABEL.needs_sme.toLowerCase()} — the client report can go once it is resolved.`
              : `${specialistCount} findings are ${VERDICT_LABEL.needs_sme.toLowerCase()} — the client report can go once they are resolved.`
          }
        />
      )}

      {globalError && (
        <div className="px-6 pt-3">
          <Alert
            tone="error"
            size="sm"
            title="Something didn't save"
            action={
              <Button size="sm" variant="ghost" onClick={dismissGlobalError}>
                Dismiss
              </Button>
            }
          >
            {globalError.message} Your last change was rolled back.
          </Alert>
        </div>
      )}

      <div className="relative min-h-0 flex-1">
        {view === "findings" && (
          <SplitPane
            storageKey="review:findings"
            defaultFraction={0.62}
            minFraction={0.4}
            maxFraction={0.78}
            ariaLabel="Resize PDF and rail"
            left={
              <div className="relative h-full min-h-0">
                <PdfSurface
                  documentUrl={review.fplUrl}
                  label={review.fplName}
                  subtitle="Final Printed Label"
                  page={page}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  focusRequest={focusReq}
                  findings={review.findings}
                  activeFindingId={activeId}
                  onSelectFinding={focusFinding}
                  cursors={live.cursors}
                  drawMode={drawMode}
                  sections={PAGE_SECTIONS}
                  onDrawComplete={(r) => {
                    setDrawMode(false)
                    setDrawContext({
                      bbox: r.bbox,
                      sectionKey: r.section?.key,
                      sectionLabel: r.section?.label,
                    })
                    setAddFindingOpen(true)
                  }}
                  onDrawCancel={() => setDrawMode(false)}
                />
                {drawMode && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-20 z-30 flex justify-center px-4">
                    <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-surface/95 px-3 py-1.5 text-text-2 shadow-e3 backdrop-blur">
                      <span className="grid size-5 place-items-center rounded-full bg-brand text-white font-id text-[10px] font-semibold">
                        +
                      </span>
                      <span className="text-caption">
                        Drag a box on the printed label to add a finding
                      </span>
                      <span className="text-text-3">·</span>
                      <kbd className="rounded-sm border border-border px-1 py-0.5 font-id text-[10px] text-text-2">
                        Esc
                      </kbd>
                      <span className="text-caption text-text-3">to cancel</span>
                    </div>
                  </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center px-4">
                  <PdfControls
                    findings={orderedFindings}
                    activeFindingId={activeId}
                    onSelectFinding={focusFinding}
                    page={page}
                    pageCount={review.pageCount}
                    onPageChange={setPage}
                    zoom={zoom}
                    onZoomChange={setZoomManual}
                    onZoomToActive={zoomToActive}
                    isFramed={preFocusZoom != null}
                  />
                </div>
              </div>
            }
            right={
              <FindingsRail
                findings={review.findings}
                loading={false}
                activeFindingId={activeId}
                expandedFindingId={expandedId}
                editingFindingId={editingId}
                canDispose
                onSelect={focusFinding}
                onToggleExpand={(id) =>
                  setExpandedId((cur) => (cur === id ? null : id))
                }
                onEnterEdit={(id) => setEditingId(id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(findingId, patch) => {
                  editNarrative.mutate({ reviewId, findingId, patch })
                  setEditingId(null)
                  toast({ tone: "success", title: "Finding updated" })
                }}
                onConfirm={(id) => doVerdict(id, "confirmed")}
                onDismiss={(id) => doVerdict(id, "rejected")}
                onSendToSme={(id) => doVerdict(id, "needs_sme")}
                onMarkCorrected={(findingId) =>
                  markCorrected.mutate({ reviewId, findingId })
                }
                onOpenComments={openComments}
                onAddFinding={() => {
                  setDrawContext(null)
                  setDrawMode(true)
                }}
                searchRef={searchRef}
              />
            }
          />
        )}

        {view === "side_by_side" && (
          <SideBySide
            fplUrl={review.fplUrl}
            masterUrl={review.masterUrl}
            fplLabel={review.fplName}
            masterLabel={review.masterName}
            page={page}
            zoom={zoom}
            findings={review.findings}
            activeFindingId={activeId}
            onSelectFinding={focusFinding}
          />
        )}
      </div>

      <PeopleRoutingSheet
        open={peopleOpen}
        onOpenChange={setPeopleOpen}
        review={{
          ...review,
          assignment: {
            ...review.assignment,
            assignee: people?.assignee
              ? {
                  userId: people.assignee.userId,
                  name: people.assignee.name,
                  role: people.assignee.role,
                  sinceISO: review.updatedAt,
                }
              : null,
            events: [...events],
          },
        }}
        colleagues={[...colleagues]}
        onAssign={async (assigneeId, name, role, note) => {
          try {
            await assignReview.mutateAsync({
              reviewId,
              assigneeId,
              assigneeName: name,
              assigneeRole: role,
              note,
            })
            toast({ tone: "success", title: `Handed to ${name}` })
          } catch {
            toast({ tone: "error", title: "Handoff failed" })
          }
        }}
        onReturnToPool={async () => {
          try {
            await returnToPool.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Returned to the pool" })
          } catch {
            toast({ tone: "error", title: "Return failed" })
          }
        }}
        onAdvance={async () => {
          try {
            await advanceStage.mutateAsync({ reviewId })
            toast({ tone: "success", title: "Advanced" })
          } catch {
            toast({ tone: "error", title: "Advance failed" })
          }
        }}
        onSkipSecondPass={async () => {
          try {
            await skipSecondPass.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Second pass skipped" })
          } catch {
            toast({ tone: "error", title: "Skip failed" })
          }
        }}
        onArchive={async () => {
          try {
            await archiveReview.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Archived" })
          } catch {
            toast({ tone: "error", title: "Archive failed" })
          }
        }}
      />

      <MessagesSheet
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        messages={messages}
        findings={orderedFindings}
        activeFindingId={activeId}
        clientAttached={Boolean(review.client)}
        onFocusFinding={focusFinding}
        onSend={({ body, findingId }) => {
          postMessage.mutate({ reviewId, body, findingId })
        }}
        onMarkRead={(findingId) =>
          markMessagesRead.mutate({ reviewId, findingId })
        }
      />

      <AddFindingDialog
        open={addFindingOpen}
        onOpenChange={(o) => {
          setAddFindingOpen(o)
          if (!o) setDrawContext(null)
        }}
        onSubmit={submitAddFinding}
        rules={RULE_CATALOG}
        drawContext={drawContext}
      />

      <ReviewShortcuts open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  )
}

function WorkspaceSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-0 flex-col" aria-busy="true">
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-3">
        <Skeleton className="size-8 rounded-sm" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-14" />
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-8 w-40 rounded-full" />
          <Skeleton className="size-8 rounded-sm" />
          <Skeleton className="size-8 rounded-sm" />
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex-1 bg-[color:var(--surface-sunken)] p-6">
          <Skeleton className="mx-auto h-full max-w-[720px] rounded-sm" />
        </div>
        <div className="w-[440px] border-l border-border bg-surface p-4">
          <Skeleton className="mb-3 h-5 w-24" />
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
        </div>
      </div>
      <span className="sr-only">Loading review</span>
    </div>
  )
}
